import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { emailHistoryAPI, EmailHistory, EmailStatus } from '../../services/api';
import { 
  ArrowLeftIcon,
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon,
  QueueListIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const EmailHistoryDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [email, setEmail] = useState<EmailHistory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmail();
  }, [id]);

  const fetchEmail = async () => {
    try {
      setLoading(true);
      const data = await emailHistoryAPI.getById(parseInt(id!));
      setEmail(data);
    } catch (error) {
      console.error('Error fetching email:', error);
      alert('Failed to load email');
      navigate('/clients/emails');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: EmailStatus) => {
    const badges = {
      SENT: { bg: 'bg-success-100', text: 'text-success-800', icon: CheckCircleIcon, label: 'Sent' },
      FAILED: { bg: 'bg-error-100', text: 'text-error-800', icon: XCircleIcon, label: 'Failed' },
      SCHEDULED: { bg: 'bg-primary-100', text: 'text-primary-800', icon: ClockIcon, label: 'Scheduled' },
      QUEUED: { bg: 'bg-warning-100', text: 'text-warning-800', icon: QueueListIcon, label: 'Queued' },
      DRAFT: { bg: 'bg-neutral-100', text: 'text-neutral-800', icon: DocumentTextIcon, label: 'Draft' },
    };

    const badge = badges[status];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="h-5 w-5 mr-2" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!email) {
    return null;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/clients/emails')}
          className="inline-flex items-center text-sm text-neutral-500 hover:text-neutral-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Email History
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-neutral-900">Email Details</h1>
          {getStatusBadge(email.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Subject */}
          <div className="bg-white shadow-xl rounded-2xl p-8 border border-neutral-200">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">{email.subject}</h2>

            {/* Recipients */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">To:</label>
                <div className="flex flex-wrap gap-2">
                  {email.to.map((recipient, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm"
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      {recipient}
                    </span>
                  ))}
                </div>
              </div>

              {email.cc && email.cc.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">CC:</label>
                  <div className="flex flex-wrap gap-2">
                    {email.cc.map((recipient, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-neutral-100 text-neutral-800 rounded-full text-sm"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        {recipient}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {email.bcc && email.bcc.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">BCC:</label>
                  <div className="flex flex-wrap gap-2">
                    {email.bcc.map((recipient, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 bg-neutral-100 text-neutral-800 rounded-full text-sm"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-1" />
                        {recipient}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="border-t border-neutral-200 pt-6">
              <label className="block text-sm font-medium text-neutral-700 mb-4">Message:</label>
              <div className="prose max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-neutral-700 leading-relaxed">
                  {email.body}
                </pre>
              </div>
            </div>

            {/* Error Message */}
            {email.error && (
              <div className="mt-6 p-4 bg-error-50 border border-error-200 rounded-lg">
                <h3 className="text-sm font-medium text-error-800 mb-2">Error Details:</h3>
                <p className="text-sm text-error-700">{email.error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Metadata */}
          <div className="bg-white shadow-xl rounded-2xl p-6 border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Email Information</h3>

            <div className="space-y-4">
              {/* Sent At */}
              {email.sentAt && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Sent At
                  </label>
                  <div className="flex items-center text-sm text-neutral-900">
                    <CalendarIcon className="h-4 w-4 mr-2 text-neutral-400" />
                    {new Date(email.sentAt).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Scheduled For */}
              {email.scheduledFor && !email.sentAt && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Scheduled For
                  </label>
                  <div className="flex items-center text-sm text-neutral-900">
                    <ClockIcon className="h-4 w-4 mr-2 text-neutral-400" />
                    {new Date(email.scheduledFor).toLocaleString()}
                  </div>
                </div>
              )}

              {/* Created At */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Created At
                </label>
                <div className="flex items-center text-sm text-neutral-900">
                  <CalendarIcon className="h-4 w-4 mr-2 text-neutral-400" />
                  {new Date(email.createdAt).toLocaleString()}
                </div>
              </div>

              {/* Sent By */}
              {email.sentBy && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Sent By
                  </label>
                  <div className="flex items-center text-sm text-neutral-900">
                    <UserIcon className="h-4 w-4 mr-2 text-neutral-400" />
                    {email.sentBy.firstName} {email.sentBy.lastName}
                  </div>
                </div>
              )}

              {/* Template */}
              {email.template && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Template Used
                  </label>
                  <div className="flex items-center text-sm text-neutral-900">
                    <DocumentTextIcon className="h-4 w-4 mr-2 text-neutral-400" />
                    {email.template.name}
                  </div>
                </div>
              )}

              {/* Client */}
              {email.client && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Client
                  </label>
                  <button
                    onClick={() => navigate(`/clients/${email.client!.id}`)}
                    className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    {email.client.name}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white shadow-xl rounded-2xl p-6 border border-neutral-200">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">Actions</h3>

            <div className="space-y-3">
              {email.client && (
                <button
                  onClick={() => navigate(`/clients/${email.client!.id}`)}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
                >
                  View Client
                </button>
              )}

              <button
                onClick={() => navigate('/clients/emails')}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-semibold"
              >
                Back to List
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailHistoryDetail;

